import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FuseNavItemType } from "@fuse/core/FuseNavigation/types/FuseNavItemType";
import { useLocation, useNavigate } from "react-router";
import useNavigationItems from "@/components/theme-layouts/components/navigation/hooks/useNavigationItems";
import { useNavbarContext } from "@/components/theme-layouts/components/navbar/contexts/NavbarContext/useNavbarContext";
import { enqueueSnackbar } from "notistack";
import { HeaderTab, HeaderTabsContext } from "./HeaderTabsContextObject";

type FooterTabsProviderProps = {
	readonly children: ReactNode;
};

const MAX_TABS = 5;

export function FooterTabsProvider({ children }: FooterTabsProviderProps) {
	const { flattenData } = useNavigationItems();
	const { openNavbar } = useNavbarContext();
	const location = useLocation();
	const navigate = useNavigate();
	const [tabs, setTabs] = useState<HeaderTab[]>([]);
	const [activeTabId, setActiveTabId] = useState<string | null>(null);
	const lastPathRef = useRef(location.pathname);
	const tabsRef = useRef<HeaderTab[]>(tabs);

	useEffect(() => {
		tabsRef.current = tabs;
	}, [tabs]);

	useEffect(() => {
		const currentItem = flattenData.find((item) => item.url === location.pathname);

		if (!currentItem?.id || !currentItem.url || currentItem.disabled || currentItem.hidden) {
			setActiveTabId(null);
			lastPathRef.current = location.pathname;
			return;
		}

		const existingTabs = tabsRef.current;
		const tabExists = existingTabs.some((t) => t.id === currentItem.id);

		if (!tabExists && existingTabs.length >= MAX_TABS) {
			if (location.pathname !== lastPathRef.current)
				enqueueSnackbar(`Você atingiu o limite de abas abertas: ${MAX_TABS}`, {
					variant: "warning",
				});

			navigate(lastPathRef.current, { replace: true });
			return;
		}

		if (!tabExists)
			setTabs((prevTabs) => {
				if (prevTabs.some((t) => t.id === currentItem.id)) return prevTabs;

				return [
					...prevTabs,
					{
						id: currentItem.id,
						title: currentItem.title ?? "",
						url: currentItem.url,
					},
				];
			});

		setActiveTabId(currentItem.id);
		lastPathRef.current = location.pathname;
	}, [flattenData, location.pathname, navigate]);

	const openFromNavItem = useCallback((item: FuseNavItemType) => {
		if (!item?.id) return;
	}, []);

	const setActiveTab = useCallback(
		(id: string) => {
			const tab = tabs.find((t) => t.id === id);

			if (!tab) return;

			setActiveTabId(id);

			if (location.pathname !== tab.url) navigate(tab.url);
		},
		[navigate, tabs, location.pathname],
	);

	const clearAllTabs = useCallback(() => {
		setTabs([]);
		setActiveTabId(null);
		navigate("/");
	}, [navigate]);

	const closeTab = useCallback(
		(id: string) => {
			setTabs((prev) => {
				const index = prev.findIndex((t) => t.id === id);

				if (index === -1) return prev;

				const closingTabUrl = prev[index]?.url;
				const nextTabs = prev.filter((t) => t.id !== id);
				const isClosingActive = activeTabId === id || (closingTabUrl && location.pathname === closingTabUrl);

				if (isClosingActive) {
					const fallback = nextTabs[index - 1] ?? nextTabs[0] ?? null;

					setActiveTabId(fallback?.id ?? null);
					lastPathRef.current = fallback?.url ?? "/";

					if (!fallback) openNavbar();

					const destino = fallback?.url ?? "/";
					if (location.pathname !== destino) navigate(destino);
				}

				return nextTabs;
			});
		},
		[activeTabId, navigate, location.pathname, openNavbar],
	);

	const value = useMemo(
		() => ({
			tabs,
			activeTabId,
			openFromNavItem,
			setActiveTab,
			closeTab,
			clearAllTabs,
		}),
		[tabs, activeTabId, openFromNavItem, setActiveTab, closeTab, clearAllTabs],
	);

	return <HeaderTabsContext value={value}>{children}</HeaderTabsContext>;
}
